import { Stack, Typography, useTheme } from "@mui/material"
import { ChartsTooltipContainer, useAxesTooltip } from "@mui/x-charts"
import CircleIcon from "@mui/icons-material/Circle"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"

import type { FieldQualityMap } from "types/quality.types"

import ChartTooltipCard from "widgets/plots/mui/ChartTooltipCard"
import { getFieldFromSeriesId } from "../configuration/helpers"

interface MuiLineTooltipProps {
  trigger?: "axis" | "item" | "none" | undefined
  fieldQualityMap?: FieldQualityMap
  tooltipFormatter?: (value: number) => string
}

const MuiLineTooltip = ({
  trigger,
  fieldQualityMap,
  tooltipFormatter,
}: MuiLineTooltipProps) => {
  const axes = useAxesTooltip()
  const { t } = useTranslation()
  const theme = useTheme()

  // Tooltip only for X axes, one axis of type "_time"
  // Not handling multiple axes or multiple time axes
  // Also not handling trigger "item"
  const timeAxis = axes !== null && axes[0].axisId === "_time" ? axes[0] : null

  const formattedDate = timeAxis?.axisValue
    ? DateTime.fromJSDate(timeAxis.axisValue as Date).toFormat("yyyy-MM-dd HH:mm")
    : ""

  const timeMillis = timeAxis?.axisValue
    ? DateTime.fromJSDate(timeAxis.axisValue as Date).toMillis()
    : null

  return (
    <ChartsTooltipContainer trigger={trigger}>
      {trigger === "axis" && timeAxis && (
        <ChartTooltipCard title={formattedDate}>
          {timeAxis.seriesItems.map((s) => {
            if (s.formattedValue === null) return null

            const field = getFieldFromSeriesId(s.seriesId as string)
            const qualityInfo =
              fieldQualityMap && timeMillis !== null && field
                ? fieldQualityMap[timeMillis]?.[field]
                : undefined

            return (
              <Stack key={s.seriesId} spacing={0}>
                <Stack direction="row" gap={1} alignItems="center" width={"100%"}>
                  <CircleIcon sx={{ fontSize: 8, color: s.color }} />
                  <Stack
                    direction="row"
                    justifyContent={"space-between"}
                    width={"100%"}
                    gap={2}
                  >
                    <Typography>{s.formattedLabel}</Typography>
                    <Typography>
                      {tooltipFormatter && typeof s.value === "number"
                        ? tooltipFormatter(s.value)
                        : s.formattedValue}
                    </Typography>
                  </Stack>
                </Stack>
                {qualityInfo?.quality && qualityInfo.quality !== "healthy" && (
                  <Typography
                    variant="caption"
                    sx={{
                      pl: 2,
                      fontSize: 10,
                      fontStyle: "italic",
                      color: theme.palette.quality[qualityInfo.quality]?.main,
                    }}
                  >
                    {t(`quality.${qualityInfo.quality.toUpperCase()}`)}
                  </Typography>
                )}
              </Stack>
            )
          })}
        </ChartTooltipCard>
      )}
    </ChartsTooltipContainer>
  )
}

export default MuiLineTooltip
